import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScanFace, Camera, CheckCircle2, AlertCircle, ChevronLeft, RefreshCw, ShieldCheck } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { showToast } from '@/components/ui/toaster';
import { attendanceService } from '@/services/attendance.service';
import { loadModels, getDescriptor } from '@/lib/face';
import { useWorkspace } from '@/hooks/useWorkspace';

type Phase = 'loading' | 'ready' | 'capturing' | 'done' | 'error';

export default function FaceEnrollPage() {
    const { t } = useI18n();
    const navigate = useNavigate();
    const { currentWorkspace } = useWorkspace();
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const [phase, setPhase] = useState<Phase>('loading');
    const [errorMsg, setErrorMsg] = useState<string>('');
    const [enrolled, setEnrolled] = useState<boolean>(false);
    const [statusKnown, setStatusKnown] = useState(false);

    // Cek status enrollment + load model + buka kamera.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await attendanceService.faceStatus();
                const data = (res.data as any) || {};
                if (!cancelled) setEnrolled(!!data.enrolled);
            } catch {
                // abaikan — anggap belum enroll
            } finally {
                if (!cancelled) setStatusKnown(true);
            }

            const ok = await loadModels();
            if (cancelled) return;
            if (!ok) {
                setErrorMsg(t('face.error_models'));
                setPhase('error');
                return;
            }
            await startCamera(cancelled);
        })();

        return () => {
            cancelled = true;
            stopCamera();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startCamera = async (cancelled = false) => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
                audio: false,
            });
            if (cancelled) {
                stream.getTracks().forEach((tr) => tr.stop());
                return;
            }
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play().catch(() => {});
            }
            setPhase('ready');
        } catch {
            setErrorMsg(t('face.error_camera'));
            setPhase('error');
        }
    };

    const stopCamera = () => {
        streamRef.current?.getTracks().forEach((tr) => tr.stop());
        streamRef.current = null;
        if (videoRef.current) videoRef.current.srcObject = null;
    };

    const handleCapture = async () => {
        if (!videoRef.current) return;
        setPhase('capturing');
        try {
            const descriptor = await getDescriptor(videoRef.current);
            if (!descriptor || descriptor.length !== 128) {
                setPhase('ready');
                showToast({ variant: 'destructive', description: t('face.no_face') });
                return;
            }
            if (!currentWorkspace?._id) {
                setPhase('ready');
                showToast({ variant: 'destructive', description: t('face.enroll_failed') });
                return;
            }
            await attendanceService.enrollFace(currentWorkspace._id, Array.from(descriptor));
            stopCamera();
            setEnrolled(true);
            setPhase('done');
            showToast({ variant: 'success', description: t('face.enroll_success') });
        } catch (err: any) {
            setPhase('ready');
            showToast({
                variant: 'destructive',
                description: err?.response?.data?.error || t('face.enroll_failed'),
            });
        }
    };

    return (
        <div className="px-4 pt-4 max-w-md mx-auto space-y-3">
            <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 text-sm font-bold text-gray-500 hover:text-gray-700"
            >
                <ChevronLeft className="h-4 w-4" />
                {t('common.back')}
            </button>

            <div className="text-center pt-1">
                <div className="h-14 w-14 mx-auto rounded-2xl flex items-center justify-center bg-brand/10 mb-2">
                    <ScanFace className="h-7 w-7 text-brand" />
                </div>
                <h1 className="text-lg font-black text-gray-900">{t('face.enroll_title')}</h1>
                <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto leading-relaxed">
                    {t('face.enroll_subtitle')}
                </p>
            </div>

            {statusKnown && enrolled && phase !== 'done' && (
                <div className="px-3 py-2.5 rounded-xl flex items-start gap-2" style={{ backgroundColor: '#e8f3e8' }}>
                    <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#3d8b37' }} />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold" style={{ color: '#2d6529' }}>{t('face.already_enrolled_title')}</p>
                        <p className="text-[11px] text-gray-600 mt-0.5">{t('face.already_enrolled_body')}</p>
                    </div>
                </div>
            )}

            <div
                className="relative rounded-3xl overflow-hidden bg-gray-900 aspect-[3/4] flex items-center justify-center"
                style={{ border: '1px solid #e0d6cc' }}
            >
                {phase === 'done' ? (
                    <div className="text-center text-white px-6">
                        <CheckCircle2 className="h-14 w-14 mx-auto text-green-400 mb-3" />
                        <p className="text-sm font-bold">{t('face.done_title')}</p>
                        <p className="text-xs text-white/70 mt-1">{t('face.done_body')}</p>
                    </div>
                ) : phase === 'error' ? (
                    <div className="text-center text-white px-6">
                        <AlertCircle className="h-12 w-12 mx-auto text-amber-400 mb-3" />
                        <p className="text-sm font-bold">{errorMsg || t('face.error_generic')}</p>
                    </div>
                ) : (
                    <>
                        <video
                            ref={videoRef}
                            playsInline
                            muted
                            className="w-full h-full object-cover scale-x-[-1]"
                        />
                        {/* Oval guide */}
                        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                            <div className="w-[62%] h-[72%] rounded-[50%] border-2 border-white/70 border-dashed" />
                        </div>
                        {phase === 'loading' && (
                            <div className="absolute inset-0 bg-gray-900/70 flex flex-col items-center justify-center text-white">
                                <RefreshCw className="h-6 w-6 animate-spin mb-2" />
                                <p className="text-xs">{t('face.loading_models')}</p>
                            </div>
                        )}
                        {phase === 'capturing' && (
                            <div className="absolute inset-0 bg-gray-900/60 flex flex-col items-center justify-center text-white">
                                <RefreshCw className="h-6 w-6 animate-spin mb-2" />
                                <p className="text-xs">{t('face.analyzing')}</p>
                            </div>
                        )}
                    </>
                )}
            </div>

            {phase === 'done' ? (
                <Button onClick={() => navigate('/')} size="xl" className="w-full font-black">
                    {t('face.back_home')}
                </Button>
            ) : phase === 'error' ? (
                <Button onClick={() => { setPhase('loading'); setErrorMsg(''); startCamera(); }} size="xl" variant="outline" className="w-full font-black">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {t('common.try_again')}
                </Button>
            ) : (
                <Button
                    onClick={handleCapture}
                    disabled={phase !== 'ready'}
                    size="xl"
                    className="w-full font-black"
                >
                    <Camera className="h-5 w-5 mr-2" />
                    {phase === 'capturing' ? t('face.analyzing') : enrolled ? t('face.recapture') : t('face.capture')}
                </Button>
            )}

            <p className="text-[10px] text-gray-400 text-center leading-relaxed px-4">
                {t('face.privacy_note')}
            </p>
        </div>
    );
}
